import { useRef, type KeyboardEvent } from "react";

import { HelperText } from "@/components/Alert";
import { BrandLogo, brandColor, logoUrl } from "@/components/brands";
import { CodeField } from "@/components/CodeField";
import { ExternalIcon } from "@/components/icons";
import { Divider, GUTTER } from "@/components/SiteChrome";
import { RobotSticker } from "@/components/stickers";
import { Button } from "@/components/ui/button";
import { CLIENTS, type Client } from "@/content/clients";
import { cn } from "@/lib/utils";

// The setup section (DESIGN.md: Tab, Logo field). The client strip is the
// only selector: one cell per client. The steps below change with it, over a
// slowly scrolling field of the selected client's logo.

function LogoField({ id }: { id: string }) {
  const url = logoUrl(id);
  if (!url) return null;
  return (
    <div aria-hidden="true" className="logo-field">
      <div
        className="logo-field-scroll"
        style={{ "--logo": `url(${url})`, "--brand": brandColor(id) } as React.CSSProperties}
      />
    </div>
  );
}

function Steps({ client, url }: { client: Client; url: string }) {
  return (
    <ol className="m-0 p-0 list-none grid content-start gap-zsd-xl">
      {client.steps(url).map((step, i) => (
        <li key={i} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-zsd-md">
          <span aria-hidden="true" className="font-zsd-label-md text-zsd-label-md text-zsd-primary pt-[0.3em]">
            {String(i + 1).padStart(2, "0")}
          </span>
          <div className="min-w-0 grid gap-zsd-sm">
            <p className="m-0">
              <span className="sr-only">Step {i + 1}: </span>
              {step.text}
            </p>
            {step.code && <CodeField value={step.code} label={step.codeLabel ?? `${client.name} setup`} />}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function ClientSetup({
  url,
  selected,
  onSelect: setSelected,
}: {
  url: string;
  selected: number;
  onSelect: (index: number) => void;
}) {
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const last = CLIENTS.length - 1;
    const next =
      e.key === "ArrowRight" || e.key === "ArrowDown" ? (selected === last ? 0 : selected + 1)
      : e.key === "ArrowLeft" || e.key === "ArrowUp" ? (selected === 0 ? last : selected - 1)
      : e.key === "Home" ? 0
      : e.key === "End" ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    setSelected(next);
    tabs.current[next]?.focus();
  }

  const client = CLIENTS[selected]!;
  return (
    <section id="setup" aria-labelledby="setup-title" className="relative">
      <LogoField id={client.id} />

      <div
        role="tablist"
        aria-label="Choose your AI client"
        onKeyDown={onKeyDown}
        className="relative grid grid-cols-9 [&>*+*]:line-l"
      >
        {CLIENTS.map((c, i) => {
          const active = i === selected;
          return (
            <button
              key={c.id}
              ref={(el) => {
                tabs.current[i] = el;
              }}
              role="tab"
              id={`tab-${c.id}`}
              aria-selected={active}
              aria-controls="setup-panel"
              tabIndex={active ? 0 : -1}
              onClick={() => setSelected(i)}
              style={{ "--brand": brandColor(c.id) } as React.CSSProperties}
              className={cn(
                "brand ease relative flex flex-col items-center justify-center gap-zsd-sm cursor-pointer",
                "py-zsd-lg md:px-zsd-sm md:py-zsd-xl",
                active
                  ? "tab-bar bg-zsd-surface-raised text-zsd-on-surface-raised"
                  : "text-zsd-muted hover:text-zsd-on-surface hover:bg-zsd-surface-raised",
              )}
            >
              <BrandLogo id={c.id} size={24} className="brand-logo" />
              {/* Phones show the marks only; the name stays for screen readers. */}
              <span className="sr-only md:not-sr-only font-zsd-label-md text-zsd-label-md uppercase">{c.name}</span>
            </button>
          );
        })}
      </div>

      <Divider />

      <div
        role="tabpanel"
        id="setup-panel"
        aria-labelledby={`tab-${client.id}`}
        key={client.id}
        style={{ "--brand": brandColor(client.id) } as React.CSSProperties}
        className={cn(
          GUTTER,
          "fade-in relative py-zsd-section grid items-start md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-zsd-3xl",
        )}
      >
        <div className="min-w-0">
          <BrandLogo id={client.id} size={32} className="text-[var(--brand)] mb-zsd-lg" />
          <h2 id="setup-title" className="m-0 font-zsd-headline-lg text-zsd-headline-lg">
            Set up {client.name}
          </h2>
          <p className="m-0 mt-zsd-md font-zsd-body-lg text-zsd-body-lg text-zsd-muted">
            Checked against {client.name}'s own documentation.
          </p>
          {client.oneClick && (
            <div className="mt-zsd-xl">
              <Button asChild>
                <a href={client.oneClick.href(url)}>
                  {client.oneClick.label} <ExternalIcon size={16} />
                </a>
              </Button>
              <HelperText>Or follow the steps.</HelperText>
            </div>
          )}
          <div className="mt-zsd-xl">
            {client.note && <HelperText className="mt-0">{client.note}</HelperText>}
            <HelperText>
              Source:{" "}
              <a href={client.docs} target="_blank" rel="noopener noreferrer">
                {client.name} MCP documentation
              </a>
            </HelperText>
          </div>
          <RobotSticker size={52} className="hidden md:block tilt-l mt-zsd-3xl" />
        </div>
        <Steps client={client} url={url} />
      </div>
    </section>
  );
}
